import {
  IImperansClinicOption,
  IImperansFranchiseOption,
  IImperansReferencePick,
} from "../../Interfaces/IImperansReference";

/**
 * The pure rules behind the Imperans franchise and clinic pickers (Epic 6303, Feature CRM3):
 * how a list row is shown, whether a stored pick is still offered, what the region may suggest,
 * and what step 3 says while sites lack their clinic. No React, no I/O — the tests drive these.
 */

/** One row as the picker shows it: the Imperans id, the name, and the line printed beneath it. */
export interface IReferencePickerOption {
  id: number;
  label: string;
  secondary?: string | null;
}

export const STALE_PICK_MESSAGE = "Imperans no longer offers this — pick again.";
export const IMPERANS_UNAVAILABLE_MESSAGE =
  "Imperans could not be reached, so this list is unavailable.";
export const COUNTRY_NEEDED_MESSAGE = "Choose the client's country first.";
export const FRANCHISE_REQUIRED_MESSAGE = "Choose the owning Imperans franchise.";
export const CLINIC_REQUIRED_MESSAGE = "Choose the servicing clinic from the Imperans list.";

export const franchiseOptions = (list: IImperansFranchiseOption[]): IReferencePickerOption[] =>
  list.map((franchise) => ({
    id: franchise.imperansFranchiseId,
    label: franchise.name || `Franchise ${franchise.imperansFranchiseId}`,
    secondary: franchise.shortCode,
  }));

/** Each clinic shows its franchise beside its name (story 6373); the list arrives most used first. */
export const clinicOptions = (list: IImperansClinicOption[]): IReferencePickerOption[] =>
  list.map((clinic) => ({
    id: clinic.imperansFranchiseOfficeId,
    label: clinic.name || `Clinic ${clinic.imperansFranchiseOfficeId}`,
    secondary: clinic.franchiseName,
  }));

/**
 * Whether a stored pick is still offered. `null` when nothing is picked, or when the list could not
 * be fetched — then nothing can be said either way, and the pick is neither confirmed nor stale.
 */
export const isPickOffered = (
  id: number | null | undefined,
  offered: IReferencePickerOption[] | null,
): boolean | null => {
  if (id === null || id === undefined || !offered) return null;
  return offered.some((option) => option.id === id);
};

/** A pick Imperans no longer offers is flagged on the step and the review until re-picked (story 6374). */
export const isPickStale = (
  id: number | null | undefined,
  offered: IReferencePickerOption[] | null,
): boolean => isPickOffered(id, offered) === false;

export const pickFromOption = (option: IReferencePickerOption | null): IImperansReferencePick | null =>
  option ? { id: option.id, name: option.label } : null;

/**
 * The region may pre-select a franchise while nothing is picked yet, but only one Imperans actually
 * offers — and it is never sent in the pick's place (story 6372).
 */
export const franchiseSuggestedByRegion = (
  regionImperansFranchiseID: number | null | undefined,
  offered: IReferencePickerOption[],
): IReferencePickerOption | null => {
  if (regionImperansFranchiseID === null || regionImperansFranchiseID === undefined) return null;
  return offered.find((option) => option.id === regionImperansFranchiseID) ?? null;
};

/** A CSV clinic column is matched to the list by exact name, ignoring case and spacing (story 6373). */
export const clinicByName = (
  name: string | null | undefined,
  offered: IReferencePickerOption[],
): IReferencePickerOption | null => {
  const wanted = `${name ?? ""}`.trim().toLowerCase();
  if (!wanted) return null;
  return offered.find((option) => option.label.trim().toLowerCase() === wanted) ?? null;
};

export const sitesWithoutClinic = (
  sites: { imperansFranchiseOfficeID?: number | null }[] | null | undefined,
): number =>
  (sites || []).filter(
    (site) => site.imperansFranchiseOfficeID === null || site.imperansFranchiseOfficeID === undefined,
  ).length;

/** Step 3's advance message once at least one site exists: every site needs its clinic (story 6373). */
export const step3ClinicMessage = (
  sites: { imperansFranchiseOfficeID?: number | null }[] | null | undefined,
): string | null => {
  const missing = sitesWithoutClinic(sites);
  if (!missing) return null;
  return `Choose a servicing clinic for every site (${missing} left).`;
};
